import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { DailyLetter, CoupleSettings } from '../types';
import {
  X,
  Eye,
  Clock,
  User,
  Smartphone,
  Shield,
  Lock,
  Mail,
  CheckCircle2,
  Calendar,
  Sparkles,
  Globe,
  MapPin,
  Laptop,
  Check,
} from 'lucide-react';
import { formatDateTimeSpanish, formatDateSpanish } from '../utils/storage';

interface LetterAuditModalProps {
  letter: DailyLetter | null;
  settings: CoupleSettings;
  isOpen: boolean;
  onClose: () => void;
  onTogglePrivacy?: (letterId: string) => void;
}

export const LetterAuditModal: React.FC<LetterAuditModalProps> = ({
  letter,
  settings,
  isOpen,
  onClose,
  onTogglePrivacy,
}) => {
  if (!isOpen || !letter) return null;

  const views = letter.views || [];
  const isOpened = Boolean(letter.isRead || letter.lastOpenedAt || letter.firstOpenedAt || views.length > 0);
  const latestView = views[0];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/35 backdrop-blur-md">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-lg rounded-3xl bg-white border border-[#F0DDE4] p-6 sm:p-7 shadow-2xl text-[#302B30] flex flex-col max-h-[85vh] overflow-hidden"
          id="modal-letter-audit"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full text-[#77717A] hover:text-[#302B30] bg-[#FFF4F7] hover:bg-[#FFE8F0] transition-all cursor-pointer"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Header */}
          <div className="flex items-center gap-3 mb-4 pr-8">
            <div className="w-11 h-11 rounded-2xl bg-[#FFF4F7] border border-[#F0DDE4] flex items-center justify-center shrink-0">
              <Eye className="w-5 h-5 text-[#E83E78]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase font-bold tracking-widest text-[#E83E78]">
                  Registro de Apertura
                </span>
                {letter.isPrivate ? (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-50 text-purple-700 border border-purple-200 flex items-center gap-1">
                    <Lock className="w-2.5 h-2.5" /> Privada
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    🌐 Pública
                  </span>
                )}
              </div>
              <h2 className="text-lg sm:text-xl font-bold font-['Playfair_Display',serif] text-[#302B30] line-clamp-1">
                {letter.title}
              </h2>
            </div>
          </div>

          {/* Toggle Privacy Quick Action */}
          {onTogglePrivacy && (
            <div className="mb-4 p-3 rounded-2xl bg-[#FFF4F7] border border-[#F0DDE4] flex items-center justify-between gap-3">
              <div>
                <span className="text-xs font-bold text-[#302B30] block">Visibilidad de la Carta:</span>
                <span className="text-[11px] text-[#77717A]">
                  {letter.isPrivate ? 'Solo accesible con credenciales autorizadas' : 'Visible para todos'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onTogglePrivacy(letter.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border flex items-center gap-1.5 cursor-pointer shrink-0 ${
                  letter.isPrivate
                    ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200'
                    : 'bg-[#FFE8F0] hover:bg-[#FFD9E6] text-[#E83E78] border-[#E85D8E]/30'
                }`}
              >
                {letter.isPrivate ? (
                  <>
                    <Globe className="w-3.5 h-3.5" />
                    <span>Pasar a Pública</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-3.5 h-3.5" />
                    <span>Pasar a Privada</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Read Status Summary Card with IP & Device Forensics */}
          <div className="p-4 rounded-2xl bg-[#F8F5F7] border border-[#F0DDE4] mb-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-500 font-medium">Estado de Lectura:</span>
              {isOpened ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Abierta y Leída</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                  <Mail className="w-3.5 h-3.5 text-amber-600" />
                  <span>Aún no ha sido abierta</span>
                </span>
              )}
            </div>

            {isOpened && (
              <>
                <div className="flex items-start justify-between gap-2 pt-2 border-t border-slate-200 text-xs">
                  <span className="text-slate-500 flex items-center gap-1.5 shrink-0">
                    <Clock className="w-3.5 h-3.5 text-amber-600" />
                    <span>Fecha y hora de apertura:</span>
                  </span>
                  <span className="text-right font-semibold text-slate-900 font-mono text-[11px]">
                    {formatDateTimeSpanish(letter.lastOpenedAt || letter.firstOpenedAt || '')}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-2 pt-2 border-t border-slate-200 text-xs">
                  <span className="text-slate-500 flex items-center gap-1.5 shrink-0">
                    <User className="w-3.5 h-3.5 text-rose-500" />
                    <span>Identificado como:</span>
                  </span>
                  <span className="font-bold text-amber-700">
                    {letter.openedBy || settings.partner2Name || 'Destinatario'}
                  </span>
                </div>

                {/* Device & IP Details from Latest Record */}
                {latestView && (
                  <div className="pt-2 border-t border-slate-200 text-xs space-y-1.5">
                    {latestView.deviceModel && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500 flex items-center gap-1.5 shrink-0">
                          <Smartphone className="w-3.5 h-3.5 text-sky-600" />
                          <span>Dispositivo:</span>
                        </span>
                        <span className="text-slate-800 text-right truncate max-w-[240px] font-medium">
                          {latestView.deviceModel}
                        </span>
                      </div>
                    )}

                    {latestView.ip && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500 flex items-center gap-1.5 shrink-0">
                          <Globe className="w-3.5 h-3.5 text-emerald-600" />
                          <span>Dirección IP:</span>
                        </span>
                        <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-800 font-semibold">
                          {latestView.ip}
                        </span>
                      </div>
                    )}

                    {latestView.city && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-slate-500 flex items-center gap-1.5 shrink-0">
                          <MapPin className="w-3.5 h-3.5 text-rose-500" />
                          <span>Ubicación:</span>
                        </span>
                        <span className="text-slate-800 text-right font-medium">
                          {latestView.city}{latestView.country ? `, ${latestView.country}` : ''}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {letter.firstOpenedAt && letter.lastOpenedAt && letter.firstOpenedAt !== letter.lastOpenedAt && (
                  <div className="flex items-start justify-between gap-2 pt-2 border-t border-slate-200 text-xs text-slate-500">
                    <span>Primera apertura:</span>
                    <span className="text-slate-800 text-right font-mono text-[11px]">
                      {formatDateTimeSpanish(letter.firstOpenedAt)}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Timeline of views */}
          <div className="flex-1 overflow-y-auto pr-1 space-y-2.5">
            <div className="flex items-center justify-between text-xs text-slate-600 font-semibold px-1">
              <span>Historial de Visualizaciones (Dispositivo & IP)</span>
              <span className="text-[11px] font-mono text-amber-700">
                {views.length} {views.length === 1 ? 'registro' : 'registros'}
              </span>
            </div>

            {views.length === 0 ? (
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-center text-xs text-slate-500">
                {isOpened ? (
                  <p>Abierta por {letter.openedBy || 'el destinatario'}. Las visualizaciones detalladas se actualizarán conforme vuelva a abrirse.</p>
                ) : (
                  <p>La carta permanece en su sobre sellado esperando ser descubierta.</p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {views.map((view, idx) => (
                  <div
                    key={`${view.openedAt}-${idx}`}
                    className="p-3 rounded-xl bg-slate-50 hover:bg-slate-100/80 border border-slate-200 flex flex-col gap-1.5 text-xs transition-colors"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-slate-900 font-medium">
                        <User className="w-3 h-3 text-rose-500" />
                        <span className="text-amber-800 font-bold">{view.viewerName}</span>
                      </div>
                      <span className="text-right text-[10px] text-slate-500 font-mono">
                        {formatDateTimeSpanish(view.openedAt)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-2 flex-wrap text-[11px]">
                      {view.deviceModel && (
                        <div className="flex items-center gap-1 text-slate-600">
                          <Smartphone className="w-3 h-3 text-slate-400" />
                          <span className="truncate max-w-[200px]">{view.deviceModel}</span>
                        </div>
                      )}

                      {view.ip && (
                        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-white text-slate-800 border border-slate-200 font-medium">
                          IP: {view.ip}
                        </span>
                      )}
                    </div>

                    {view.city && (
                      <div className="text-[10px] text-slate-500 flex items-center gap-1">
                        <MapPin className="w-2.5 h-2.5 text-rose-500" />
                        <span>{view.city}{view.country ? `, ${view.country}` : ''}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer Action */}
          <div className="pt-4 mt-2 border-t border-slate-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition-all cursor-pointer"
            >
              Cerrar
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

